import React, { useMemo } from "react";
import PropTypes from "prop-types";
import NeoCard from "../../../../../components/Common/NeoCard";
import NeoBadge from "../../../../../components/Common/NeoBadge";
import { formatIsoDatePtBr } from "../../../../../utils/tenantLifecycle";

const ACTION_LABELS = {
    create_tenant: "Tenant criada",
    change_plan: "Plano alterado",
    subscription_plan_changed: "Plano alterado",
    trial_period_updated: "Trial alterado",
    block_tenant: "Acesso bloqueado",
    tenant_blocked: "Acesso bloqueado",
    unblock_tenant: "Acesso liberado",
    tenant_temporary_liberation: "Liberação temporária",
    register_manual_payment: "Pagamento manual",
    payment_registered_manual: "Pagamento manual",
    generate_invoice: "Cobrança gerada",
    invoice_created_manual: "Cobrança gerada",
    admin_impersonation: "Acesso suporte",
    support_ticket_created: "Ticket aberto",
    support_ticket_resolved: "Ticket resolvido",
};

const ACTION_COLORS = {
    create_tenant: "success",
    change_plan: "info",
    subscription_plan_changed: "info",
    trial_period_updated: "warning",
    block_tenant: "danger",
    tenant_blocked: "danger",
    unblock_tenant: "success",
    tenant_temporary_liberation: "warning",
    register_manual_payment: "success",
    payment_registered_manual: "success",
    generate_invoice: "accent",
    invoice_created_manual: "accent",
    admin_impersonation: "secondary",
    support_ticket_created: "info",
    support_ticket_resolved: "success",
};

const parseDate = (value) => {
    if (!value) return 0;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const DetailTimeline = ({ tenant, logs }) => {
    const events = useMemo(() => {
        const syntheticEvents = [
            tenant.createdAt && {
                id: "created",
                action: "create_tenant",
                date: tenant.createdAt,
                title: "Tenant criada",
                description: `Academia ${tenant.name || "sem nome"} cadastrada na plataforma.`,
                user: tenant.ownerName || "Sistema",
            },
            tenant.trialStartDate && {
                id: "trial_start",
                action: "trial_period_updated",
                date: `${tenant.trialStartDate}T12:00:00`,
                title: "Início do trial",
                description: `Trial configurado para iniciar em ${formatIsoDatePtBr(tenant.trialStartDate)}.`,
                user: "Sistema",
            },
            tenant.trialEndsAt && {
                id: "trial_end",
                action: "trial_period_updated",
                date: `${tenant.trialEndsAt}T12:00:00`,
                title: "Fim do trial",
                description: `Trial configurado para encerrar em ${formatIsoDatePtBr(tenant.trialEndsAt)}.`,
                user: "Sistema",
            },
            tenant.accessBlockedAt && {
                id: "blocked",
                action: "block_tenant",
                date: tenant.accessBlockedAt,
                title: "Acesso bloqueado",
                description: tenant.accessBlockMessage || tenant.accessBlockReason || "Acesso da academia bloqueado.",
                user: "Sistema",
            },
        ].filter(Boolean);

        const auditEvents = logs.map((log) => ({
            id: `log_${log.id}`,
            action: log.action,
            date: log.rawDate || log.date,
            title: ACTION_LABELS[log.action] || log.action || "Evento",
            description: log.desc || "—",
            user: log.user || "Sistema",
        }));

        // Remove eventos sintéticos que já existem nos logs de auditoria (evita duplicação)
        const auditActions = new Set(auditEvents.map((e) => e.action));
        const dedupedSynthetic = syntheticEvents.filter((e) => !auditActions.has(e.action));

        return [...dedupedSynthetic, ...auditEvents]
            .sort((a, b) => parseDate(b.date) - parseDate(a.date));
    }, [tenant, logs]);

    return (
        <NeoCard
            title="Timeline Operacional"
            subtitle="Eventos principais, alterações comerciais e ações administrativas da academia."
            thinBorder={true}
            bodyClassName="p-4"
        >
            {events.length === 0 ? (
                <div className="text-center text-muted py-4 font-size-13">
                    Nenhum evento encontrado para esta academia.
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {events.map((event) => (
                        <div key={event.id} className="d-flex gap-3 pb-3 border-bottom">
                            <div className="pt-1">
                                <span className="d-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: 34, height: 34 }}>
                                    <i className="ph-light ph-clock-clockwise text-muted fs-5" />
                                </span>
                            </div>
                            <div className="flex-grow-1">
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                    <strong className="text-dark font-size-14">{event.title}</strong>
                                    <NeoBadge color={ACTION_COLORS[event.action] || "secondary"}>
                                        {event.action || "evento"}
                                    </NeoBadge>
                                </div>
                                <div className="text-muted font-size-12 mb-1">{event.description}</div>
                                <div className="text-muted font-size-11">
                                    {formatDateTime(event.date)} · {event.user}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </NeoCard>
    );
};

DetailTimeline.propTypes = {
    tenant: PropTypes.object.isRequired,
    logs: PropTypes.array.isRequired,
};

export default DetailTimeline;
